import React, { useEffect, useRef, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button, Row, useModelStateContext } from 'saladsoft-primitive';
import Select from './Select';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import ServiceHelper from 'stores/ServiceHelper';
import { TextField } from '@mui/material';
import { SearchParams, SearchKey } from 'types/CommonTypes';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { koKR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/ko';
import 'dayjs/locale/ja';
import i18n from 'i18n';
import { getUrlParam } from 'libs/helper/utils';

const FilterBox = ({ onSearch }: { onSearch: (searchParams: SearchParams) => void }) => {
  const { t } = useTranslation();
  const keywordRef = useRef<any>(null);

  const status = [
    { key: 'ALL', value: t('All Status') },
    { key: 'RECEIVED', value: t('Preparing') },
    { key: 'PROCESSING', value: t('Processing') },
    { key: 'CLEANSED', value: t('Validation Pending') },
    { key: 'COMPLETED', value: t('Completed') },
    { key: 'VALIDATION_ERROR', value: t('Validation Error') },
    { key: 'USER_VERIFIED', value: t('Verified') },
    { key: 'TRANSMISSION_FAILED', value: t('Transmission Failed') },
  ];

  const searchKeys: SearchKey[] = [
    { key: '', value: t('Select Key') },
    { key: 'orderId', value: t('Order ID') },
    { key: 'trackingNo', value: t('Tracking No') },
    { key: 'itemName', value: t('Title(Origin)') },
    { key: 'itemOption', value: t('Option') },
    { key: 'optionCode', value: t('Option Code') },
    { key: 'customsTitle', value: t('Cleansing Title') },
    { key: 'hsCode', value: t('HS Code') },
    { key: 'subMasterBl', value: t('Sub Master BL') },
    { key: 'masterBl', value: t('Master BL') },
    { key: 'itemCode', value: t('Item Code') },
    { key: 'material', value: t('Material') },
    { key: 'itemUrl', value: t('Item Url') },
    { key: 'realWeight', value: t('Weight') },
    { key: 'unitPrice', value: t('Unit Price') },
    { key: 'quantity', value: t('Quantity') },
  ];

  const size = [
    { key: 20, value: t('20 items') },
    { key: 50, value: t('50 items') },
    { key: 100, value: t('100 items') },
  ];

  const { updateServiceModel, serviceModel, stateModel, setState, setMultipleState } =
    useModelStateContext<SearchParams>();

  const [searchParams] = useSearchParams();
  const [params, setParams] = useState<SearchParams>();
  const [categories, setCategories] = useState<{ key: number; value: string }[]>([]);
  const [categoryId, setCategoryId] = useState<number>();

  const handleSearch = () => {
    if (serviceModel.current) {
      // 검색 키가 없는데 검색어가 있는 경우
      if (serviceModel.current.searchKey === '' && serviceModel.current.searchValue !== '') {
        toast.warning(t('Please select a search key'));
        return;
      }

      // 검색 키는 있는데 검색어가 없는 경우
      if (serviceModel.current.searchKey !== '' && serviceModel.current.searchValue === '') {
        toast.warning(t('Please enter a search value'));
        return;
      }

      // 현재 URL 파라미터를 가져와서 기본값으로 사용
      const currentParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });

      // serviceModel의 값으로 덮어씌움
      const newSearchParams: Partial<SearchParams> = {
        ...currentParams,
        ...serviceModel.current,
      };

      onSearch(newSearchParams as SearchParams);
    }
  };

  const resetFilter = () => {
    const defaultParams = {
      startDate: '',
      endDate: '',
      category: '',
      status: '',
      size: 50,
      page: 1,
      searchKey: '',
      searchValue: '',
      //sort는 리셋 대상이 아님(필터 아님)
      // sort: '',
    };

    setMultipleState(defaultParams);
    setParams(defaultParams as SearchParams);

    if (keywordRef.current) {
      keywordRef.current.querySelector('input').value = '';
    }

    setCategoryId(-1);

    let sortTemp = getUrlParam('sort');
    sortTemp = sortTemp ? '?sort=' + sortTemp : '';
    // 파라미터를 사일런트하게 제거
    window.history.replaceState(null, '', window.location.pathname + sortTemp);
  };

  const handleStatusChange = (value: { key: string; value: string }) => {
    updateServiceModel('status', value.key);
  };

  const fetchCategories = () => {
    ServiceHelper.categoryService?.getCategories().then((res) => {
      if (res.status === 200) {
        const categoryRecords = [
          { key: -1, value: t('All Categories') },
          ...(res.data?.map((category) => ({
            key: category.id ?? 0,
            value: category.name ?? '',
          })) || []),
        ];
        setCategories(categoryRecords);
      }
    });
  };

  const getDatePicker = (type: 'startDate' | 'endDate') => {
    return (
      <DatePicker
        format="YYYY-MM-DD"
        value={
          stateModel?.[type] === '' ? null : stateModel?.[type] ? dayjs(stateModel?.[type]) : null
        }
        onChange={(newValue) => {
          if (serviceModel.current) {
            const newDate = newValue?.format('YYYY-MM-DD') ?? '';

            if (type === 'startDate' && stateModel?.endDate === '') {
              setState('endDate', newValue?.format('YYYY-MM-DD') ?? '');
            }

            if (type === 'endDate' && stateModel?.startDate === '') {
              setState('startDate', newValue?.format('YYYY-MM-DD') ?? '');
            }

            if (type === 'endDate' && serviceModel.current?.startDate && newDate) {
              if (dayjs(newDate).isBefore(dayjs(serviceModel.current.startDate))) {
                toast.error(t('End date must be after start date'));
                setState(type, stateModel?.startDate ?? '');

                return;
              }
            }
            if (type === 'startDate' && serviceModel.current?.endDate && newDate) {
              if (dayjs(newDate).isAfter(dayjs(serviceModel.current.endDate))) {
                toast.error(t('Start date must be before end date'));
                setState(type, stateModel?.endDate ?? '');

                return;
              }
            }

            if (newDate) {
              setState(type, newDate);
            } else {
              setState(type, '');
            }
          }
        }}
        sx={{
          '& .MuiStack-root': {
            paddingTop: '0px',
          },
        }}
        slots={{
          textField: (params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps?.endAdornment}

                    {stateModel?.[type] && (
                      <CloseIcon
                        style={{ cursor: 'pointer', marginLeft: 5 }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setState(type, '');
                        }}
                      />
                    )}
                  </React.Fragment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  height: '48px',
                  paddingTop: '0px',
                },
                '& .MuiStack-root': {
                  paddingTop: '0px !important',
                },
              }}
            />
          ),
        }}
      />
    );
  };

  useEffect(() => {
    fetchCategories();
  }, [i18n.language]);

  useEffect(() => {
    const category = searchParams.get('category');
    const status = searchParams.get('status');
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const size = searchParams.get('size');
    const page = searchParams.get('page');
    const searchKey = searchParams.get('searchKey');
    const searchValue = searchParams.get('searchValue');
    const sort = searchParams.get('sort');

    if (keywordRef.current) {
      keywordRef.current.querySelector('input').value = searchValue ?? '';
    }

    setParams({
      category: category != null ? category : '-1',
      status: status != null ? status : '',
      startDate: startDate != null ? startDate : '',
      endDate: endDate != null ? endDate : '',
      size: size != null ? parseInt(size) : 50,
      page: page != null ? parseInt(page) : 1,
      searchKey: searchKey != null ? searchKey : '',
      searchValue: searchValue != null ? searchValue : '',
      sort: sort != null ? sort : '',
    });

    setCategoryId(category != null ? Number(category) : -1);
    setMultipleState({
      category: category != null ? category : '-1',
      status: status != null ? status : '',
      startDate: startDate != null ? startDate : '',
      endDate: endDate != null ? endDate : '',
      size: size != null ? parseInt(size) : 50,
      page: page != null ? parseInt(page) : 1,
      searchKey: searchKey != null ? searchKey : '',
      searchValue: searchValue != null ? searchValue : '',
      sort: sort != null ? sort : '',
    });
  }, [searchParams]);

  return (
    <div style={{ paddingTop: 10 }}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={i18n.language}
        localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
      >
        <Row gap="5px" justifyContent="flex-start">
          <Row gap="5px">
            <div>{getDatePicker('startDate')}</div>
            <div>{getDatePicker('endDate')}</div>
          </Row>
          <Select
            key={`category-select-${params?.category}`}
            style={{ minWidth: 200 }}
            itemBoxStyle={{ maxHeight: 300, overflowY: 'auto' }}
            defaultKey={categoryId}
            useRound={false}
            items={categories}
            placeHolder={`${t('Select Category')}`}
            onChange={(value) => {
              updateServiceModel('category', value.key);
            }}
          />
          <Select
            key={`status-select-${i18n.language}`}
            style={{ minWidth: 200 }}
            itemBoxStyle={{ maxHeight: 300, overflowY: 'auto' }}
            defaultKey={params?.status?.toUpperCase() || 'ALL'}
            useRound={false}
            items={status}
            onChange={handleStatusChange}
            placeHolder={`${t('Select Status')}`}
          />
          <Select
            style={{ minWidth: 100 }}
            defaultKey={params?.size ?? 50}
            useRound={false}
            items={size}
            onChange={(value) => {
              updateServiceModel('size', value.key);
            }}
            placeHolder={`${t('Select Size')}`}
          />

          <Row marginBottom="0px" width="330px" gap="5px">
            <Select
              style={{ minWidth: 200 }}
              itemBoxStyle={{ maxHeight: 300, overflowY: 'auto' }}
              defaultKey={params?.searchKey ?? ''}
              useRound={false}
              items={searchKeys}
              onChange={(value) => {
                updateServiceModel('searchKey', value.key);
              }}
              placeHolder={`${t('Select Key')}`}
            />
            <TextField
              ref={keywordRef}
              defaultValue={params?.searchValue ?? ''}
              onChange={(e: any) => {
                updateServiceModel('searchValue', e.target.value);
              }}
              placeholder={t('Keyword')}
              style={{ width: 200 }}
              sx={{
                '& .sc-blHHSb': {
                  marginBottom: '0px',
                },
                '& .MuiOutlinedInput-root > input': {
                  padding: '10px 10px',
                },
              }}
            />
          </Row>
          <Row justifyContent="flex-end">
            <Button style={{ minHeight: 43, borderRadius: 5, minWidth: 70 }} onClick={handleSearch}>
              {t('Search')}
            </Button>
            <>
              <Button
                type="link"
                style={{ minHeight: 43, borderRadius: 5, minWidth: 90, padding: '0 10px' }}
                onClick={resetFilter}
              >
                {t('Reset')}
              </Button>
            </>
          </Row>
        </Row>
      </LocalizationProvider>
    </div>
  );
};

export default FilterBox;
